import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings as SettingsIcon, Ruler, Info, Sun, Moon, UserCog, KeyRound, Trash2, Loader2, Lock, ShieldAlert } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

export const Settings = () => {
  const [isMetric, setIsMetric] = useState(true);
  const { theme, setTheme } = useTheme();
  const { user, changePassword, deleteAccount } = useAuth();
  const navigate = useNavigate();

  // Change password dialog state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  // Delete account dialog state
  const [delOpen, setDelOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [delLoading, setDelLoading] = useState(false);

  const resetPwdDialog = () => {
    setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); setPwdLoading(false);
  };
  const resetDelDialog = () => {
    setDelPassword(""); setDelConfirm(""); setDelLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("New passwords do not match"); return; }
    if (newPwd === currentPwd) { toast.error("New password must be different from current"); return; }
    setPwdLoading(true);
    try {
      await changePassword(currentPwd, newPwd);
      toast.success("Password changed successfully");
      setPwdOpen(false);
      resetPwdDialog();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPwdLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (delConfirm !== "DELETE") { toast.error('Please type "DELETE" exactly to confirm'); return; }
    if (!delPassword) { toast.error("Please enter your password"); return; }
    setDelLoading(true);
    try {
      await deleteAccount(delPassword, delConfirm);
      toast.success("Account deleted");
      navigate("/login");
    } catch (err) {
      toast.error(err.message);
      setDelLoading(false);
    }
  };

  useEffect(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem("eventpro-unit-system");
    if (saved) {
      setIsMetric(saved === "metric");
    }
  }, []);

  const handleUnitChange = (metric) => {
    setIsMetric(metric);
    localStorage.setItem("eventpro-unit-system", metric ? "metric" : "imperial");
    toast.success(`Unit system set to ${metric ? "Metric" : "Imperial"}`);
  };

  const handleThemeChange = (isDark) => {
    const newTheme = isDark ? "dark" : "light";
    setTheme(newTheme);
    toast.success(`Theme set to ${isDark ? "Dark" : "Light"} mode`);
  };

  return (
    <Layout 
      title="Settings" 
      subtitle="Configure your evntkit preferences"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Appearance */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Sun className="h-5 w-5 text-yellow-400" />
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-sm">
              <div className="flex items-center gap-3">
                <div>
                  <Label className="text-base font-semibold">Theme</Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark mode
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Sun className={`h-4 w-4 ${theme === 'light' ? 'text-yellow-400' : 'text-muted-foreground'}`} />
                <Switch
                  checked={theme === "dark"}
                  onCheckedChange={handleThemeChange}
                  data-testid="theme-toggle"
                />
                <Moon className={`h-4 w-4 ${theme === 'dark' ? 'text-cyan-400' : 'text-muted-foreground'}`} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-sm border transition-colors cursor-pointer ${
                theme === 'light' ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-border/50'
              }`} onClick={() => handleThemeChange(false)}>
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="h-4 w-4 text-yellow-400" />
                  <p className="font-semibold">Light Mode</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Bright theme for well-lit environments
                </p>
              </div>
              <div className={`p-4 rounded-sm border transition-colors cursor-pointer ${
                theme === 'dark' ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-border/50'
              }`} onClick={() => handleThemeChange(true)}>
                <div className="flex items-center gap-2 mb-2">
                  <Moon className="h-4 w-4 text-cyan-400" />
                  <p className="font-semibold">Dark Mode</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Easy on the eyes in low-light conditions
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unit System */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Ruler className="h-5 w-5 text-cyan-400" />
              Unit System
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-sm">
              <div className="flex items-center gap-3">
                <div>
                  <Label className="text-base font-semibold">Default Unit System</Label>
                  <p className="text-sm text-muted-foreground">
                    Used for all calculators and measurements
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${!isMetric ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                  Imperial
                </span>
                <Switch
                  checked={isMetric}
                  onCheckedChange={handleUnitChange}
                  data-testid="unit-system-toggle"
                />
                <span className={`text-sm ${isMetric ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                  Metric
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-sm border transition-colors ${
                !isMetric ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-border/50'
              }`}>
                <p className="font-semibold mb-2">Imperial</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Length: feet, inches</li>
                  <li>• Area: square feet</li>
                  <li>• Weight: pounds</li>
                  <li>• Volume: gallons</li>
                </ul>
              </div>
              <div className={`p-4 rounded-sm border transition-colors ${
                isMetric ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-border/50'
              }`}>
                <p className="font-semibold mb-2">Metric</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Length: meters, centimeters</li>
                  <li>• Area: square meters</li>
                  <li>• Weight: kilograms</li>
                  <li>• Volume: litres</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Management */}
        <Card className="tactical-card" data-testid="account-management-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <UserCog className="h-5 w-5 text-cyan-400" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {user && (
              <div className="p-4 bg-secondary/50 rounded-sm">
                <p className="text-sm text-muted-foreground">Signed in as</p>
                <p className="font-semibold">{user.name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            )}

            {/* Change Password */}
            <div className="flex items-center justify-between gap-4 p-4 border border-border/50 rounded-sm">
              <div>
                <p className="font-semibold">Change Password</p>
                <p className="text-sm text-muted-foreground">Update your account password</p>
              </div>
              <Dialog open={pwdOpen} onOpenChange={(o) => { setPwdOpen(o); if (!o) resetPwdDialog(); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="open-change-password">
                    <KeyRound className="h-4 w-4 mr-2" />
                    Change
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                    <DialogDescription>
                      Enter your current password and choose a new one (min 6 characters).
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-pwd">Current Password</Label>
                      <Input
                        id="current-pwd"
                        type="password"
                        value={currentPwd}
                        onChange={(e) => setCurrentPwd(e.target.value)}
                        data-testid="change-password-current"
                        required
                        disabled={pwdLoading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-pwd">New Password</Label>
                      <Input
                        id="new-pwd"
                        type="password"
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        data-testid="change-password-new"
                        required
                        minLength={6}
                        disabled={pwdLoading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-pwd">Confirm New Password</Label>
                      <Input
                        id="confirm-pwd"
                        type="password"
                        value={confirmPwd}
                        onChange={(e) => setConfirmPwd(e.target.value)}
                        data-testid="change-password-confirm"
                        required
                        minLength={6}
                        disabled={pwdLoading}
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="ghost" onClick={() => setPwdOpen(false)} disabled={pwdLoading}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={pwdLoading} data-testid="change-password-submit" className="bg-cyan-600 hover:bg-cyan-700">
                        {pwdLoading ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                        ) : (
                          <><Lock className="h-4 w-4 mr-2" />Update Password</>
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Delete Account */}
            <div className="flex items-center justify-between gap-4 p-4 border border-red-500/40 bg-red-500/5 rounded-sm">
              <div>
                <p className="font-semibold text-red-400">Delete Account</p>
                <p className="text-sm text-muted-foreground">Permanently remove your account and all data. This cannot be undone.</p>
              </div>
              <Dialog open={delOpen} onOpenChange={(o) => { setDelOpen(o); if (!o) resetDelDialog(); }}>
                <DialogTrigger asChild>
                  <Button variant="destructive" data-testid="open-delete-account">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-400">
                      <ShieldAlert className="h-5 w-5" />
                      Delete Account
                    </DialogTitle>
                    <DialogDescription>
                      This will permanently delete your account, floor plans, and feedback. This action <strong>cannot be undone</strong>.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDeleteAccount} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="del-pwd">Confirm with your password</Label>
                      <Input
                        id="del-pwd"
                        type="password"
                        value={delPassword}
                        onChange={(e) => setDelPassword(e.target.value)}
                        data-testid="delete-account-password"
                        required
                        disabled={delLoading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="del-confirm">Type <span className="font-mono text-red-400">DELETE</span> to confirm</Label>
                      <Input
                        id="del-confirm"
                        type="text"
                        value={delConfirm}
                        onChange={(e) => setDelConfirm(e.target.value)}
                        data-testid="delete-account-confirm"
                        placeholder="DELETE"
                        required
                        disabled={delLoading}
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="ghost" onClick={() => setDelOpen(false)} disabled={delLoading}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="destructive"
                        disabled={delLoading || delConfirm !== "DELETE"}
                        data-testid="delete-account-submit"
                      >
                        {delLoading ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                        ) : (
                          <><Trash2 className="h-4 w-4 mr-2" />Permanently Delete</>
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg uppercase">
              <Info className="h-5 w-5 text-cyan-400" />
              About
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-secondary/50 rounded-sm">
              <h3 className="font-semibold text-lg mb-2">evntkit</h3>
              <p className="text-sm text-muted-foreground">
                Planning and delivery tools for event professionals, covering all stages of event management:
                planning, execution, and problem-solving.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-sm">
                <p className="text-2xl font-bold text-cyan-400">4</p>
                <p className="text-xs text-muted-foreground">Planning Tools</p>
              </div>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                <p className="text-2xl font-bold text-yellow-400">4</p>
                <p className="text-xs text-muted-foreground">Execution Tools</p>
              </div>
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-sm">
                <p className="text-2xl font-bold text-purple-400">3</p>
                <p className="text-xs text-muted-foreground">Problem Solvers</p>
              </div>
            </div>

            <div className="text-center pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground">
                Built with React, FastAPI, and AI-powered features
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Feature List */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-cyan-400 mb-2">Planning Suite</p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Room Capacity Calculator</li>
                  <li>• Catering "Waste-Zero" Estimator with Sustainability Mode</li>
                  <li>• Staffing & Shift Planner</li>
                  <li>• AV Tech Specs Tool</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-yellow-400 mb-2">Execution Suite</p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Master Sync Timer with shareable codes</li>
                  <li>• Digital Decibel Meter with 90dB alerts</li>
                  <li>• 2D Floor Plan Tool</li>
                  <li>• Lux (Lighting) Meter</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-purple-400 mb-2">Problem Solver Suite</p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• AI Contract Red-Flag Scanner</li>
                  <li>• AI Emergency Script Generator</li>
                  <li>• Currency & Unit Converter</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Settings;
